import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { useDate, useMoney, useT } from '../i18n/index.js';
import { Badge, Banner, Button, Card, Field, Spinner } from '../components/ui.js';
import { IconWallet } from '../components/icons.js';

interface Line {
  variantId: string;
  sku: string;
  size: string;
  qty: number;
}

/**
 * CHECKOUT.
 *
 * Tres cosas que aqui no se negocian:
 *  1. Las TALLAS se eligen aqui, no en el editor: el diseno no esta atado a una
 *     talla (DEC-09) y la tirada mixta es el caso normal en B2B.
 *  2. El presupuesto se muestra con su DESGLOSE y su CADUCIDAD.
 *  3. La confirmacion lleva `Idempotency-Key`: en movil con mala red, sin ella
 *     se duplican pedidos.
 */
export function CheckoutPage() {
  const { designId = '' } = useParams();
  const [params] = useSearchParams();
  const t = useT();
  const money = useMoney();
  const formatDate = useDate();
  const navigate = useNavigate();

  const [lines, setLines] = useState<Line[]>([]);
  const [variants, setVariants] = useState<Array<{ id: string; sku: string; size: string; available: boolean }>>([]);
  const [designVersionId, setDesignVersionId] = useState<string>('');
  const [quote, setQuote] = useState<Awaited<ReturnType<typeof api.createQuote>> | null>(null);
  const [decision, setDecision] = useState<Awaited<ReturnType<typeof api.checkoutDecision>> | null>(null);
  const [wallet, setWallet] = useState<{ available: boolean; balance: { totalEuros: number } | null }>({
    available: false,
    balance: null,
  });
  const [walletCents, setWalletCents] = useState(0);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Clave de idempotencia ESTABLE durante toda la pantalla: si el usuario pulsa
  // dos veces o se le va la red, es el MISMO pedido.
  const idempotencyKey = useMemo(() => `order-${designId}-${crypto.randomUUID()}`, [designId]);

  useEffect(() => {
    (async () => {
      try {
        const design = await api.getDesign(designId);
        const size = params.get('size') ?? undefined;
        const product = await api.getProduct(design.productId, { ...(size ? { size } : {}), colorId: design.colorId });
        const own = product.variants.filter((v) => v.colorId === design.colorId);
        setVariants(own);
        setDesignVersionId(design.currentVersionId);
        if (own.length > 0 && size) {
          const match = own.find((v) => v.size === size) ?? own[0]!;
          setLines([{ variantId: match.id, sku: match.sku, size: match.size, qty: 10 }]);
        }
        setWallet(await api.walletBalance());
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [designId, params]);

  // El presupuesto se recalcula cada vez que cambian las cantidades: el precio
  // por unidad depende del TOTAL del pedido, no de cada linea.
  useEffect(() => {
    if (!designVersionId || lines.length === 0) return;
    let cancelled = false;
    api
      .createQuote({ designVersionId, lines: lines.map((l) => ({ variantId: l.variantId, qty: l.qty })) })
      .then(async (result) => {
        if (cancelled) return;
        setQuote(result);
        setDecision(await api.checkoutDecision(result.quoteId));
        setError(null);
      })
      .catch((err) => !cancelled && setError((err as Error).message));
    return () => {
      cancelled = true;
    };
  }, [designVersionId, lines]);

  if (loading) return <Spinner label={t('app.loading')} />;

  const confirm = async () => {
    if (!quote) return;
    setBusy(true);
    try {
      const result = await api.confirmOrder(
        {
          quoteId: quote.quoteId,
          calculationHash: quote.calculationHash,
          walletDebitCents: walletCents,
          acceptedTerms: true,
        },
        idempotencyKey,
      );
      navigate(`/pedidos/${result.order.id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const maxWalletCents = Math.min(
    Math.round((wallet.balance?.totalEuros ?? 0) * 100),
    quote?.total.cents ?? 0,
  );

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
      <h1 className="text-2xl font-semibold">{t('checkout.title')}</h1>

      {error && <Banner tone="error" title={t('app.error')}>{error}</Banner>}

      <Card className="space-y-3">
        <h2 className="text-sm font-semibold">{t('checkout.sizes')}</h2>
        <ul className="space-y-2">
          {lines.map((line, index) => (
            <li key={line.variantId} className="flex items-center gap-2">
              <select
                value={line.variantId}
                onChange={(e) => {
                  const variant = variants.find((v) => v.id === e.target.value)!;
                  setLines((current) =>
                    current.map((l, i) =>
                      i === index ? { variantId: variant.id, sku: variant.sku, size: variant.size, qty: l.qty } : l,
                    ),
                  );
                }}
                className="toque rounded-lg border border-borde px-3 py-2 text-sm"
              >
                {variants.map((v) => (
                  <option key={v.id} value={v.id} disabled={!v.available}>
                    {v.size}
                  </option>
                ))}
              </select>
              <input
                data-test="cantidad"
                type="number"
                min={1}
                value={line.qty}
                onChange={(e) =>
                  setLines((current) =>
                    current.map((l, i) => (i === index ? { ...l, qty: Math.max(1, Number(e.target.value)) } : l)),
                  )
                }
                className="toque w-24 rounded-lg border border-borde px-3 py-2 text-sm tabular-nums"
              />
              {lines.length > 1 && (
                <button
                  onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                  className="toque rounded-lg px-3 text-sm text-error"
                >
                  {t('app.delete')}
                </button>
              )}
            </li>
          ))}
        </ul>
        <Button
          variant="secondary"
          onClick={() => {
            const usadas = new Set(lines.map((l) => l.variantId));
            const libre = variants.find((v) => !usadas.has(v.id));
            if (libre) setLines((c) => [...c, { variantId: libre.id, sku: libre.sku, size: libre.size, qty: 5 }]);
          }}
          disabled={lines.length >= variants.length}
        >
          {t('checkout.addSize')}
        </Button>
      </Card>

      {quote && (
        <Card className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">{t('pricing.breakdown')}</h2>
            <span className="text-xs text-tinta-suave">
              {t('pricing.validUntil', { date: formatDate(quote.validUntil) })}
            </span>
          </div>

          <ul className="divide-y divide-borde text-sm">
            {quote.breakdown.map((line, index) => (
              <li
                key={`${line.code}-${index}`}
                data-test="linea-precio"
                className="flex items-baseline justify-between gap-3 py-2"
              >
                <span className="min-w-0">
                  <span className="block">
                    {t(`pricing.concepts.${line.code}`, {
                      ...(line.params ?? {}),
                      surcharge: line.params?.surcharge
                        ? t(`pricing.surcharges.${line.params.surcharge}`)
                        : '',
                    })}
                  </span>
                  {/* Cada linea dice QUE REGLA la genero: un comercial puede
                      explicar el precio sin abrir el codigo. */}
                  <span className="block text-xs text-tinta-suave">
                    {line.ruleId}@{line.ruleVersion} · {line.qty} × {money(line.unit.cents)}
                  </span>
                </span>
                <span className="shrink-0 tabular-nums">{money(line.amount.cents)}</span>
              </li>
            ))}
          </ul>

          <dl className="space-y-1 border-t border-borde pt-3 text-sm">
            <div className="flex justify-between">
              <dt>{t('pricing.subtotal')}</dt>
              <dd className="tabular-nums">{money(quote.subtotal.cents)}</dd>
            </div>
            <div className="flex justify-between text-tinta-suave">
              <dt>{t('pricing.vat')}</dt>
              <dd className="tabular-nums">{money(quote.vat.cents)}</dd>
            </div>
            <div className="flex justify-between text-base font-semibold">
              <dt>{t('pricing.total')}</dt>
              <dd data-test="total" className="tabular-nums">
                {money(quote.total.cents)}
              </dd>
            </div>
          </dl>
        </Card>
      )}

      {wallet.available && quote && (
        <Card className="space-y-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <IconWallet size={18} /> {t('checkout.wallet')}
          </h2>
          <p className="text-sm text-tinta-suave">
            {t('checkout.walletBalance', { amount: money(Math.round((wallet.balance?.totalEuros ?? 0) * 100)) })}
          </p>
          <Field label={t('checkout.walletUse')}>
            <input
              type="range"
              min={0}
              max={maxWalletCents}
              step={50}
              value={walletCents}
              onChange={(e) => setWalletCents(Number(e.target.value))}
              className="w-full"
            />
          </Field>
          <p className="text-sm tabular-nums">{money(walletCents)}</p>
        </Card>
      )}

      {!wallet.available && (
        <p className="text-xs text-tinta-suave">{t('checkout.walletNone')}</p>
      )}

      {decision && (
        <Banner
          tone={decision.decision === 'PAYMENT' ? 'ok' : 'warning'}
          title={t(`checkout.decision.${decision.decision}`)}
        >
          {/* La decision SIEMPRE viaja con su explicacion, y se muestran todas
              las razones, no solo la primera. */}
          <ul className="list-disc space-y-0.5 pl-4">
            {decision.reasons.map((reason, index) => (
              <li key={index}>{t(`checkout.decision.${reason.code}`)}</li>
            ))}
          </ul>
          <p className="mt-1 text-xs">
            <Badge>
              {t('pricing.total')} ≤ {money(decision.thresholdApplied.maxTotalCents)} ·{' '}
              {decision.thresholdApplied.maxQty} uds.
            </Badge>
          </p>
        </Banner>
      )}

      <Card className="space-y-3">
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1" />
          <span>{t('checkout.terms')}</span>
        </label>
        <Button data-test="confirmar" onClick={() => void confirm()} loading={busy} disabled={!quote || !terms}>
          {t('checkout.confirm')}
        </Button>
      </Card>
    </div>
  );
}
